import {
  type AnnouncementEvent,
  type BlockedUser,
  type ChatEvent,
  type ConversationSummary,
  type DirectMessageEvent,
  type InteractionRejection,
  type MapScheduledEvent,
  type PublicUser,
  type QueuePositionEvent,
  type ThemeKey,
} from '@veriverse/shared';
import { type Room } from 'colyseus.js';
import { create } from 'zustand';
import { appendToThread, applyDmToConversations } from './dm-logic.js';
import { travelRoute } from './travel-map.js';

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  /** Waiting in the admission queue for a free seat. */
  | 'queued'
  | 'connected'
  /** The connection dropped; trying to resume with the same seat. */
  | 'reconnecting'
  | 'disconnected'
  | 'error';
/** `unknown` until the first refresh attempt has finished (avoids flashing the login screen). */
export type AuthStatus = 'unknown' | 'signed-out' | 'signed-in';

const CHAT_LOG_LIMIT = 200;

export interface InteractionTarget {
  readonly objectId: number;
  readonly objectType: string;
  /** i18n key of the action label. */
  readonly labelKey: string;
}

interface SessionState {
  auth: AuthStatus;
  user: PublicUser | undefined;
  status: ConnectionStatus;
  /** i18n key or raw reason for the last connection problem. */
  error: string | undefined;
  room: Room | undefined;
  onlineCount: number;
  /** Ids of the users who are in the office right now (not asleep at their desks). */
  onlineUserIds: ReadonlySet<string>;
  chatLog: ChatEvent[];
  /** The newest message received *live*; drives speech bubbles. History never sets it. */
  lastLiveChat: ChatEvent | undefined;
  announcement: AnnouncementEvent | undefined;
  /** Why the last chat message was not delivered (cleared on the next successful send). */
  chatNotice: 'invalid' | 'rate-limited' | undefined;
  /** Position in the admission queue while `status` is `queued`. */
  queue: QueuePositionEvent | undefined;
  /** Admin "preview on my screen" of a seasonal theme; only this browser sees it (spec §6.12). */
  themePreview: ThemeKey | 'off' | undefined;
  /** Bumped whenever the server swaps the map; the canvas then fetches the new one. */
  mapRevision: number;
  /** A geometry change that will be applied at `applyAt`. */
  mapScheduled: MapScheduledEvent | undefined;
  /** The region (office) of the map the player is in, if the map has regions (ADR-0037). */
  region: string | undefined;
  /** Bumped when the player arrives in another region (a teleport): the welcome splash shows. */
  arrivals: number;
  /** The trip whose travel-map animation is playing; the welcome splash waits for it to end. */
  travel: { from: string; to: string } | undefined;

  // Object interactions (spec §6.6)
  /** The object the "E" prompt points at, if the player stands next to one. */
  interactionTarget: InteractionTarget | undefined;
  /** Why the last interaction was refused; cleared by the next target change. */
  interactionNotice: InteractionRejection | undefined;
  calendarOpen: boolean;
  /** The mini-game panel (dance floor, ping pong table) the player has open, if any (spec §6.14). */
  gamePanel: { objectId: number; kind: 'dance' | 'pong' } | undefined;

  // Direct messages (spec §6.7)
  dmOpen: boolean;
  conversations: ConversationSummary[];
  /** userId → loaded messages of that conversation, oldest first. */
  threads: Record<string, DirectMessageEvent[]>;
  /** The conversation currently shown, if any. */
  activeDm: string | undefined;
  blocked: BlockedUser[];
  dmNotice: 'invalid' | 'rate-limited' | 'unavailable' | undefined;
  /** Known names for people we have not talked to yet (from the roster). */
  names: Record<string, string>;

  setSignedIn(user: PublicUser): void;
  setSignedOut(): void;
  setUser(user: PublicUser): void;
  setConnecting(): void;
  setConnected(room: Room): void;
  setDisconnected(error?: string): void;
  setError(error: string): void;
  setOnlineCount(count: number): void;
  setOnlineUserIds(ids: ReadonlySet<string>): void;
  setChatHistory(history: ChatEvent[]): void;
  addChat(event: ChatEvent): void;
  setAnnouncement(announcement: AnnouncementEvent | undefined): void;
  setChatNotice(notice: 'invalid' | 'rate-limited' | undefined): void;
  setQueued(position: QueuePositionEvent): void;
  setReconnecting(): void;
  setIdle(): void;
  setMapScheduled(event: MapScheduledEvent): void;
  setRegion(region: string | undefined): void;
  mapChanged(): void;
  endTravel(): void;
  setThemePreview(preview: ThemeKey | 'off' | undefined): void;
  setInteractionTarget(target: InteractionTarget | undefined): void;
  setInteractionNotice(notice: InteractionRejection | undefined): void;
  setCalendarOpen(open: boolean): void;
  setGamePanel(panel: { objectId: number; kind: 'dance' | 'pong' } | undefined): void;

  setDmOpen(open: boolean): void;
  setConversations(conversations: ConversationSummary[]): void;
  setThread(userId: string, messages: DirectMessageEvent[]): void;
  setActiveDm(userId: string | undefined): void;
  setBlocked(blocked: BlockedUser[]): void;
  setDmNotice(notice: 'invalid' | 'rate-limited' | 'unavailable' | undefined): void;
  setNames(names: Record<string, string>): void;
  /** A direct message was sent or received on this connection. */
  receiveDm(event: DirectMessageEvent): void;
  markConversationRead(userId: string): void;
}

const emptyDm: Pick<
  SessionState,
  'dmOpen' | 'conversations' | 'threads' | 'activeDm' | 'blocked' | 'dmNotice'
> = {
  dmOpen: false,
  conversations: [],
  threads: {},
  activeDm: undefined,
  blocked: [],
  dmNotice: undefined,
};

/** Bridge between the network layer, Phaser and React (spec §9.1). */
export const useSessionStore = create<SessionState>()((set) => ({
  auth: 'unknown',
  user: undefined,
  status: 'idle',
  error: undefined,
  room: undefined,
  onlineCount: 0,
  onlineUserIds: new Set<string>(),
  chatLog: [],
  lastLiveChat: undefined,
  announcement: undefined,
  chatNotice: undefined,
  queue: undefined,
  themePreview: undefined,
  mapRevision: 0,
  mapScheduled: undefined,
  region: undefined,
  arrivals: 0,
  travel: undefined,
  interactionTarget: undefined,
  interactionNotice: undefined,
  calendarOpen: false,
  gamePanel: undefined,
  ...emptyDm,
  names: {},

  setSignedIn: (user) => set({ auth: 'signed-in', user }),
  setSignedOut: () =>
    set({
      auth: 'signed-out',
      user: undefined,
      room: undefined,
      status: 'idle',
      chatLog: [],
      queue: undefined,
      ...emptyDm,
    }),
  setUser: (user) => set({ user }),
  setConnecting: () => set({ status: 'connecting', error: undefined }),
  setConnected: (room) => set({ status: 'connected', room, error: undefined, queue: undefined }),
  setDisconnected: (error) =>
    set({
      status: 'disconnected',
      room: undefined,
      error,
      announcement: undefined,
      queue: undefined,
      dmOpen: false,
      gamePanel: undefined,
      interactionTarget: undefined,
      interactionNotice: undefined,
      region: undefined,
      travel: undefined,
    }),
  setError: (error) => set({ status: 'error', room: undefined, error, queue: undefined }),
  setOnlineCount: (onlineCount) => set({ onlineCount }),
  setOnlineUserIds: (onlineUserIds) => set({ onlineUserIds }),
  setChatHistory: (history) => set({ chatLog: history.slice(-CHAT_LOG_LIMIT) }),
  addChat: (event) =>
    set((state) => ({
      chatLog: [...state.chatLog, event].slice(-CHAT_LOG_LIMIT),
      lastLiveChat: event,
    })),
  setAnnouncement: (announcement) => set({ announcement }),
  setChatNotice: (chatNotice) => set({ chatNotice }),
  setQueued: (queue) => set({ status: 'queued', queue, error: undefined }),
  // The old room object stays in the store so the canvas remains mounted while we retry.
  setReconnecting: () => set({ status: 'reconnecting' }),
  setMapScheduled: (mapScheduled) => set({ mapScheduled }),
  setRegion: (region) =>
    set((state) =>
      state.region === region
        ? state
        : {
            region,
            // Entering the office counts as arrival already; only moving between regions is new.
            arrivals:
              state.region !== undefined && region !== undefined
                ? state.arrivals + 1
                : state.arrivals,
            travel:
              state.region !== undefined &&
              region !== undefined &&
              travelRoute(state.region, region)
                ? { from: state.region, to: region }
                : undefined,
          },
    ),
  endTravel: () => set({ travel: undefined }),
  mapChanged: () =>
    // The new map's scene sets the region again; that is no arrival, so no welcome splash.
    set((state) => ({
      mapRevision: state.mapRevision + 1,
      mapScheduled: undefined,
      region: undefined,
      travel: undefined,
    })),
  setThemePreview: (themePreview) => set({ themePreview }),
  setInteractionTarget: (interactionTarget) =>
    set((state) =>
      state.interactionTarget?.objectId === interactionTarget?.objectId
        ? state
        : { interactionTarget, interactionNotice: undefined },
    ),
  setInteractionNotice: (interactionNotice) => set({ interactionNotice }),
  setCalendarOpen: (calendarOpen) => set({ calendarOpen }),
  setGamePanel: (gamePanel) => set({ gamePanel }),
  setIdle: () => set({ status: 'idle', queue: undefined, error: undefined }),

  setDmOpen: (dmOpen) => set({ dmOpen }),
  setConversations: (conversations) => set({ conversations }),
  setThread: (userId, messages) =>
    set((state) => ({ threads: { ...state.threads, [userId]: messages } })),
  setActiveDm: (activeDm) => set({ activeDm, dmNotice: undefined }),
  setBlocked: (blocked) => set({ blocked }),
  setDmNotice: (dmNotice) => set({ dmNotice }),
  setNames: (names) => set({ names }),
  receiveDm: (event) =>
    set((state) => {
      const me = state.user?.id;
      if (!me) return state;
      const other = event.senderId === me ? event.recipientId : event.senderId;
      const open = state.dmOpen ? state.activeDm : undefined;
      return {
        conversations: applyDmToConversations(
          state.conversations,
          event,
          me,
          open,
          (id) => state.names[id] ?? '?',
        ),
        threads: { ...state.threads, [other]: appendToThread(state.threads[other] ?? [], event) },
      };
    }),
  markConversationRead: (userId) =>
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.userId === userId ? { ...c, unread: 0 } : c,
      ),
    })),
}));
